/**
 * Aging Screen
 *
 * Displays an accounts-receivable aging schedule bucketed by days overdue
 * (Current, 1-30, 31-60, ... >180). Each row is a reference date snapshot.
 *
 * Data flow:
 *  - Reads `data.aging` (cached result) and `project.hasAging` from Redux.
 *  - On mount, fetches previously computed aging data if it exists but is not yet in memory.
 *  - "Calculate" dispatches `calculateAging` to the Python backend, which builds
 *    the aging parquet from the filtered master frame.
 *  - EngineToggle lets the user switch between Pandas and Polars execution engines.
 */
import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
// -------------------------------------------------------------
import { calculateAging, getAgingData } from "Redux/data/actions";
import { useAnalysis, useScenarioFlag } from "Redux/data/hooks";
import DataTable from "Components/DataTable";
import SkeletonTable from "Components/Skeleton";

const headersReceivables = [
  { text: "Reference Date", key: "Reference Date", datatype: "date" },
  { text: "Current", key: "Current", datatype: "float" },
  { text: "1-30", key: "1_30", datatype: "float" },
  { text: "31-60", key: "31_60", datatype: "float" },
  { text: "61-90", key: "61_90", datatype: "float" },
  { text: "91-120", key: "91_120", datatype: "float" },
  { text: "121-150", key: "121_150", datatype: "float" },
  { text: "151-180", key: "151_180", datatype: "float" },
  { text: ">180", key: "Over_180", datatype: "float" },
  { text: "Total", key: "Total", datatype: "float" },
];

const headersPayables = [
  { text: "Reference Date", key: "Reference Date", datatype: "date" },
  { text: "Current", key: "Current", datatype: "float" },
  { text: "1-30", key: "One_Thirty", datatype: "float" },
  { text: "Total", key: "Total", datatype: "float" },
];

// -------------------------------------------------------------
// -------------------------------------------------------------
const Aging = () => {
  const dispatch = useDispatch();
  const [calculating, setCalculating] = useState(false);

  const { data: aging, has: hasAging } = useAnalysis("aging", getAgingData);
  const hasMaster = useScenarioFlag("hasMaster");
  const projectType = useSelector((state) => state.project.projectType);
  const headers = projectType === "payables" ? headersPayables : headersReceivables;

  if (!hasMaster)
    return (
      <div className="notification is-warning is-light">
        No master frame has been built yet.
      </div>
    );

  return (
    <div>
      <div className="box" style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
        <button
          className={`button is-small ${calculating ? "is-loading" : ""}`}
          onClick={() => {
            setCalculating(true);
            dispatch(calculateAging(() => setCalculating(false)));
          }}
        >
          {hasAging ? "Recalculate" : "Calculate"} Aging
        </button>
      </div>
      {hasAging && !aging && <SkeletonTable />}
      {aging && (
        <DataTable
          data={aging.data}
          headers={headers}
          reference="Reference Date"
        />
      )}
    </div>
  );
};

export default Aging;
